/**
 * Centros de trabajo: alta, geovalla y el QR imprimible.
 *
 * El radio 0 significa "sin geovalla" (una oficina no valida ubicación). El QR
 * que se imprime apunta al endpoint dinámico: rota cada 30 s, así que lo que
 * se pega en la caseta es la URL en pantalla de una tablet o el QR estático
 * del payload — para obra sin pantalla, el estático + geovalla es el combo.
 *
 * Los campos del formulario (cascada GeoNames, pin, coordenadas, geovalla,
 * toggles) viven en `WorksiteFormFields`, compartido con el wizard.
 */
import { useCallback, useEffect, useState } from 'react';
import { AdminLayout } from './AdminLayout';
import {
  getWorksites, createWorksite, updateWorksite, registerKiosk, fetchKioskQr,
  fetchPrintableQr,
  toWorksiteCreate, toWorksiteFormValue, Worksite, WorksiteFormValue, apiMessage,
  startEvacuation, getEvacuation, markPersonLocated, markPersonMissing,
  endEvacuation, EvacuationStatus,
} from '../../services/apiClient';
import { WorksiteFormFields } from '../worksites/WorksiteFormFields';
import { Icon } from '../ui/Icon';
import { EmptyState } from '../ui/EmptyState';

const EMPTY: WorksiteFormValue = { name: '', code: '', address: '', geo_radius_meters: 150 };

export function WorksitesAdmin() {
  const [sites, setSites] = useState<Worksite[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft] = useState<WorksiteFormValue>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [evacuacion, setEvacuacion] = useState<EvacuationStatus | null>(null);
  const [evacuandoId, setEvacuandoId] = useState<number | null>(null);
  const [marcandoId, setMarcandoId] = useState<number | null>(null);
  const [cerrandoEvac, setCerrandoEvac] = useState(false);

  const load = useCallback(async () => {
    setSites(await getWorksites());
  }, []);

  useEffect(() => {
    load().catch(() => setError('No se pudieron cargar los centros'));
  }, [load]);

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setDraft(EMPTY);
    setError(null);
  };

  const openCreate = () => {
    setEditingId(null);
    setDraft(EMPTY);
    setError(null);
    setShowForm(true);
  };

  // Precarga con lo guardado (incluidos pin/ciudad/provincia); la cascada
  // GeoNames arranca vacía a propósito (ver toWorksiteFormValue).
  const openEdit = (site: Worksite) => {
    setEditingId(site.id);
    setDraft(toWorksiteFormValue(site));
    setError(null);
    setShowForm(true);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      if (editingId != null) {
        await updateWorksite(editingId, toWorksiteCreate(draft));
      } else {
        await createWorksite(toWorksiteCreate(draft));
      }
      closeForm();
      await load();
    } catch (e: unknown) {
      setError(apiMessage(e, editingId != null ? 'No se pudo guardar el centro' : 'No se pudo crear el centro'));
    } finally {
      setSaving(false);
    }
  };

  /**
   * Alta de la tablet de quiosco de este centro.
   *
   * El endpoint existía desde siempre y NO lo llamaba ninguna pantalla: en
   * todo el frontend no había una sola escritura de `kakuat-kiosk-id`, así que
   * `/kiosk` caía siempre en "Tablet sin dar de alta" salvo que alguien
   * tecleara el id en la consola del navegador del dispositivo. El flujo real
   * —admin da de alta, tablet ficha— no existía.
   *
   * Se imprime un enlace y su QR: se abre UNA vez en la tablet y ésta guarda
   * el kiosk_id y su centro.
   */
  const darDeAltaTablet = async (site: Worksite) => {
    setError(null);
    try {
      const deviceId = `tablet-${site.code}-${Date.now().toString(36)}`;
      const alta = await registerKiosk(deviceId, site.name, site.id);
      const url =
        `${window.location.origin}/kiosk?kiosk_id=${encodeURIComponent(alta.kiosk_id)}` +
        (alta.worksite_uuid ? `&worksite=${encodeURIComponent(alta.worksite_uuid)}` : '');

      // El QR lo genera el BACKEND. Con la URL incrustada en la de
      // api.qrserver.com, el kiosk_id —una credencial de fichaje— viajaba a
      // un tercero sin contrato y encima fuera de la CSP: la imagen no
      // cargaba (ronda 3, r2b10/r2b21).
      const qrDataUrl = await fetchKioskQr(site.id, url);

      const w = window.open('', '_blank');
      if (!w) return;
      w.document.write(`
        <title>Alta de tablet · ${site.name}</title>
        <div style="font-family:system-ui;text-align:center;padding:40px;max-width:640px;margin:0 auto">
          <h1 style="margin:0 0 4px">${site.name}</h1>
          <p style="margin:0 0 24px;color:#666">Abre este enlace UNA vez en la tablet del quiosco</p>
          <img src="${qrDataUrl}" width="320" height="320" alt="">
          <p style="word-break:break-all;font-family:ui-monospace,monospace;font-size:12px;color:#444;margin-top:20px">${url}</p>
          <p style="color:#b00;font-size:12px;margin-top:20px">
            Este enlace es una credencial del dispositivo: no lo publiques ni lo reenvíes.
          </p>
        </div>`);
      w.document.close();
    } catch (e: unknown) {
      setError(apiMessage(e, 'No se pudo dar de alta la tablet'));
    }
  };

  /**
   * Disparar la evacuación de un centro y pasar lista desde el panel.
   *
   * El endpoint existía desde siempre y ninguna pantalla lo llamaba: en una
   * emergencia real, el responsable con el móvil en la mano no tenía ningún
   * botón que le diera el listado de quién está en el centro (ronda 3,
   * r2b17[5]). La ronda 4 cerró el ciclo (r2b17[3]): el panel fija el
   * listado arriba, permite marcar LOCALIZADO/DESAPARECIDO por persona y
   * CERRAR la evacuación — el recuento final (pending/located/missing) es
   * la prueba de quién quedaba dentro, que es por lo que la tabla existe.
   *
   * El `confirm` del arranque es un solo toque extra — fricción mínima en
   * la emergencia, pero suficiente para que no se dispare una evacuación
   * de refilón al entrar a gestionar los centros.
   */
  const iniciarEvacuacion = async (site: Worksite) => {
    if (!confirm(`¿Iniciar la EVACUACIÓN de ${site.name}? Se genera ahora mismo el listado de personas dentro del centro.`)) return;
    setEvacuandoId(site.id);
    setError(null);
    try {
      const alta = await startEvacuation(site.id);
      setEvacuacion(await getEvacuation(alta.evacuation_id));
    } catch (e: unknown) {
      setError(apiMessage(e, 'No se pudo iniciar la evacuación'));
    } finally {
      setEvacuandoId(null);
    }
  };

  /** Marcar a una persona como localizada o desaparecida y refrescar el recuento. */
  const marcarPersona = async (userId: number, como: 'located' | 'missing') => {
    if (!evacuacion) return;
    const pregunta = como === 'located'
      ? '¿Marcar a esta persona como LOCALIZADA (está a salvo)?'
      : '¿Dar a esta persona por DESAPARECIDA? Queda registrado quién y cuándo lo declaró.';
    if (!confirm(pregunta)) return;
    setMarcandoId(userId);
    setError(null);
    try {
      if (como === 'located') {
        await markPersonLocated(evacuacion.evacuation_id, userId);
      } else {
        await markPersonMissing(evacuacion.evacuation_id, userId);
      }
      setEvacuacion(await getEvacuation(evacuacion.evacuation_id));
    } catch (e: unknown) {
      setError(apiMessage(e, 'No se pudo actualizar el estado de la persona'));
    } finally {
      setMarcandoId(null);
    }
  };

  /** Cerrar la evacuación: congela el recuento final (consultable después). */
  const cerrarEvacuacion = async () => {
    if (!evacuacion) return;
    const pendientes = evacuacion.pending_count;
    if (!confirm(
      pendientes > 0
        ? `Quedan ${pendientes} ${pendientes === 1 ? 'persona' : 'personas'} sin localizar. ¿Cerrar la evacuación de todos modos?`
        : '¿Cerrar la evacuación? Se guarda el recuento final.',
    )) return;
    setCerrandoEvac(true);
    setError(null);
    try {
      await endEvacuation(evacuacion.evacuation_id);
      setEvacuacion(await getEvacuation(evacuacion.evacuation_id));
    } catch (e: unknown) {
      setError(apiMessage(e, 'No se pudo cerrar la evacuación'));
    } finally {
      setCerrandoEvac(false);
    }
  };

  /**
   * Imprimir el QR de papel.
   *
   * El QR de PAPEL es el estático. El botón imprimía el dinámico, que caduca
   * a los 30 s: lo que se pegaba en la caseta no servía para fichar ni una
   * vez. Un centro con QR de pantalla no tiene versión imprimible y se dice.
   *
   * El PNG se pide con el apiClient (Bearer incluido): un `<img src>` directo
   * al endpoint daba 401 silencioso, imagen rota y ningún diálogo de
   * impresión (ronda 3, r2b21[4]). Con el blob en un objectURL la ventana sólo
   * lleva el QR y se imprime al cargar la imagen.
   */
  const printQr = async (site: Worksite) => {
    if (site.require_dynamic_qr) {
      alert(
        'Este centro exige QR de pantalla: rota cada 30 segundos, así que no ' +
        'hay versión para imprimir. Pon una tablet mostrando el código.'
      );
      return;
    }

    setError(null);
    let blob: Blob;
    try {
      blob = await fetchPrintableQr(site.id);
    } catch (e: unknown) {
      setError(apiMessage(e, 'No se pudo obtener el QR imprimible'));
      return;
    }

    const url = URL.createObjectURL(blob);
    const w = window.open('', '_blank');
    if (!w) {
      URL.revokeObjectURL(url);
      return;
    }
    w.document.write(`
      <title>QR · ${site.name}</title>
      <div style="font-family:system-ui;text-align:center;padding:40px">
        <h1 style="margin:0 0 4px">${site.name}</h1>
        <p style="margin:0 0 24px;color:#666">Escanea para fichar · Kakuat</p>
        <img src="${url}" width="360" height="360"
             onload="setTimeout(()=>print(),300)">
        <p style="color:#999;font-size:12px">Código: ${site.code}</p>
        <p style="color:#999;font-size:12px">Este código exige ubicación: hay que estar en el centro.</p>
      </div>`);
    w.document.close();
  };

  return (
    <AdminLayout title="Centros de trabajo">
      <div className="max-w-[960px] space-y-4">
        {error && <p className="rounded-xl bg-danger-soft text-danger px-4 py-3 text-[14px]">{error}</p>}

        {evacuacion && (
          <div className="card-surface p-5 border-2 border-danger animate-rise" role="region" aria-label="Evacuación">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-bold text-[18px] text-danger">
                  {evacuacion.status === 'active' ? 'Evacuación en curso' : 'Evacuación cerrada'} ·{' '}
                  {evacuacion.total_headcount}{' '}
                  {evacuacion.total_headcount === 1 ? 'persona' : 'personas'} dentro
                </p>
                <p className="text-[13px] text-ink-2 mt-0.5">
                  <span className="font-semibold">{evacuacion.pending_count} pendientes</span>
                  {' · '}{evacuacion.located_count} localizados
                  {' · '}{evacuacion.missing_count} desaparecidos
                  {' — '}referencia {evacuacion.evacuation_id}
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                {evacuacion.status === 'active' && (
                  <button
                    onClick={cerrarEvacuacion}
                    disabled={cerrandoEvac}
                    className="h-[36px] px-4 rounded-xl bg-danger text-white font-bold text-[13px] disabled:opacity-50"
                  >
                    {cerrandoEvac ? 'Cerrando…' : 'Cerrar evacuación'}
                  </button>
                )}
                <button
                  onClick={() => setEvacuacion(null)}
                  className="h-[36px] px-4 rounded-xl border border-line-strong font-semibold text-[13px]"
                >
                  Ocultar
                </button>
              </div>
            </div>
            {evacuacion.people.length === 0 ? (
              <p className="text-[14px] text-ink-2 mt-3">
                Nadie tiene la jornada abierta en este centro ahora mismo.
              </p>
            ) : (
              <ul className="mt-3 divide-y divide-line">
                {evacuacion.people.map((p) => (
                  <li key={p.user_id} className="flex items-center justify-between gap-3 py-2 text-[14.5px]">
                    <span className="font-semibold truncate">{p.full_name}</span>
                    <span className="flex items-center gap-2.5 shrink-0">
                      <span
                        className={`font-mono text-[11px] font-semibold rounded-full px-2.5 py-1 ${
                          p.status === 'located'
                            ? 'bg-brand-soft text-brand'
                            : p.status === 'missing'
                            ? 'bg-danger-soft text-danger'
                            : 'bg-surface-2 text-ink-2'
                        }`}
                      >
                        {p.status === 'located' ? 'localizado' : p.status === 'missing' ? 'desaparecido' : 'pendiente'}
                      </span>
                      <span className="text-ink-2 font-mono text-[12px]">
                        {p.hours_on_site} h · {p.worksite_name ?? 'sin centro'}
                      </span>
                      {evacuacion.status === 'active' && (
                        <>
                          <button
                            onClick={() => marcarPersona(p.user_id, 'located')}
                            disabled={marcandoId === p.user_id || p.status === 'located'}
                            className="h-[30px] px-3 rounded-lg border border-line-strong font-semibold text-[12px] disabled:opacity-40"
                          >
                            Localizado
                          </button>
                          <button
                            onClick={() => marcarPersona(p.user_id, 'missing')}
                            disabled={marcandoId === p.user_id || p.status === 'missing'}
                            className="h-[30px] px-3 rounded-lg border-2 border-danger text-danger font-bold text-[12px] disabled:opacity-40"
                          >
                            Desaparecido
                          </button>
                        </>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={() => (showForm ? closeForm() : openCreate())}
            className="h-[42px] px-5 rounded-xl bg-brand text-on-brand font-bold text-[14px]"
          >
            {showForm ? 'Cerrar' : '+ Alta de centro'}
          </button>
        </div>

        {showForm && (
          <div className="card-surface p-5 space-y-4 animate-rise">
            <p className="font-bold text-[16px]">{editingId != null ? 'Editar centro' : 'Alta de centro'}</p>

            <WorksiteFormFields value={draft} onChange={(p) => setDraft((d) => ({ ...d, ...p }))} variant="panel" />

            <div className="flex flex-wrap items-center gap-3">
              <button onClick={save} disabled={!draft.name || !draft.code || saving} className="btn-primary h-12 text-[15px] max-w-[280px]">
                {saving
                  ? 'Guardando…'
                  : editingId != null ? 'Guardar cambios' : 'Crear centro'}
              </button>
              <button onClick={closeForm} disabled={saving} className="h-12 px-5 rounded-xl border border-line-strong font-semibold text-[14px]">
                Cancelar
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
          {sites.map((s) => (
            <div key={s.id} className="card-surface p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-bold text-[16px] truncate">{s.name}</p>
                  <p className="font-mono text-[12px] text-ink-3">{s.code}</p>
                  {s.address && <p className="text-[13.5px] text-ink-2 mt-1 truncate">{s.address}</p>}
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span
                    className={`font-mono text-[11px] font-semibold rounded-full px-2.5 py-1 ${
                      s.require_geofence ? 'bg-brand-soft text-brand' : 'bg-surface-2 text-ink-2'
                    }`}
                  >
                    {s.require_geofence ? `geovalla ${s.geo_radius_meters} m` : 'sin geovalla'}
                  </span>
                  {s.require_dynamic_qr && (
                    <span className="font-mono text-[11px] font-semibold rounded-full px-2.5 py-1 bg-brand-soft text-brand">
                      QR pantalla
                    </span>
                  )}
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2.5">
                <button
                  onClick={() => openEdit(s)}
                  className="flex-1 min-w-[130px] h-[42px] rounded-xl border border-line-strong font-semibold text-[14px] flex items-center justify-center gap-2"
                >
                  <Icon name="pin" size={17} strokeWidth={1.8} />
                  Editar
                </button>
                <button
                  onClick={() => printQr(s)}
                  className="flex-1 min-w-[130px] h-[42px] rounded-xl border border-line-strong font-semibold text-[14px] flex items-center justify-center gap-2"
                >
                  <Icon name="file" size={17} strokeWidth={1.8} />
                  Imprimir QR
                </button>
                <button
                  onClick={() => darDeAltaTablet(s)}
                  className="flex-1 min-w-[130px] h-[42px] rounded-xl border border-line-strong font-semibold text-[14px] flex items-center justify-center gap-2"
                >
                  <Icon name="kiosk" size={17} strokeWidth={1.8} />
                  Dar de alta tablet
                </button>
                <button
                  onClick={() => iniciarEvacuacion(s)}
                  disabled={evacuandoId === s.id}
                  className="flex-1 min-w-[130px] h-[42px] rounded-xl border-2 border-danger bg-danger-soft text-danger font-bold text-[14px] flex items-center justify-center gap-2"
                >
                  <Icon name="alert" size={17} strokeWidth={1.8} />
                  {evacuandoId === s.id ? 'Evacuando…' : 'Evacuación'}
                </button>
              </div>
            </div>
          ))}
          {sites.length === 0 && !showForm && (
            <div className="md:col-span-2">
              <EmptyState
                icon="building"
                title="Aún no hay centros"
                body="Crea tu primer centro de trabajo con su dirección y, si hace falta, su geovalla. Genera el QR para fichar."
                ctaLabel="Crear centro"
                onCta={() => setShowForm(true)}
              />
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
